import { EventEmitter2 } from 'eventemitter2'

export type ColorPickerType = 'basic' | 'wheel' | 'saturation' | 'hue_slider' | 'monochrome' | 'random'

export interface RGBColor {
  r: number // 0-255
  g: number // 0-255
  b: number // 0-255
}

export interface GameConfiguration {
  rounds: number
  timePerRound: number // in milliseconds
  colorPickerSequence: ColorPickerType[] // One per round, or use 'random'
}

export interface ColorPlayerState {
  id: string
  score: number
  currentGuess: RGBColor | null
  hasSubmitted: boolean
}

export interface ColorGameState {
  phase: 'waiting' | 'configuring' | 'round_starting' | 'guessing' | 'turn_ended' | 'round_ended' | 'game_ended'
  configuration: GameConfiguration | null
  currentRound: number
  totalRounds: number
  currentTurnInRound: number // Which picker in the sequence (0-based)
  totalTurnsPerRound: number // How many pickers per round
  targetColor: RGBColor | null // Hidden from clients during guessing
  currentColorPickerType: ColorPickerType | null
  pickerBaseHue: number | null // For saturation picker - the base hue to use
  players: ColorPlayerState[]
  timeRemaining: number | null
  roundResults: RoundResult[] | null // Results of the last completed turn
}

export interface RoundResult {
  playerId: string
  guess: RGBColor
  distance: number
  points: number
  accuracy: number // 0-1, where 1 is perfect match
}

export interface GuessSubmitResult {
  success: boolean
  message: string
}

/**
 * ColorGuessing Game
 * 
 * A multiplayer game where all players simultaneously guess a color
 * chosen by the system. Points are awarded based on how close the
 * guess is to the actual color.
 */
export class ColorGuessing extends EventEmitter2 {
  private playersIds: string[]
  private state: ColorGameState
  private roundTimer: NodeJS.Timeout | null = null

  public get gameStatus(): ColorGameState {
    return {
      ...this.state,
      // Show target color so players know what to match
      targetColor: this.state.targetColor,
      players: this.state.players.map(p => ({ ...p })),
      roundResults: this.state.roundResults ? [...this.state.roundResults] : null
    }
  }

  public constructor(playersIds: string[]) {
    super({ wildcard: true })
    
    // Validate
    if (!playersIds || playersIds.length < 2) {
      throw new Error('ColorGuessing requires at least 2 players')
    }

    this.playersIds = [...playersIds]

    // Initialize game state
    this.state = {
      phase: 'waiting',
      configuration: null,
      currentRound: 0,
      totalRounds: 0,
      currentTurnInRound: 0,
      totalTurnsPerRound: 0,
      targetColor: null,
      currentColorPickerType: null,
      pickerBaseHue: null,
      players: this.playersIds.map(id => ({
        id,
        score: 0,
        currentGuess: null,
        hasSubmitted: false
      })),
      timeRemaining: null,
      roundResults: null
    }
  }

  /**
   * Start the configuration phase
   */
  public startConfiguration(): void {
    if (this.state.phase !== 'waiting') {
      return
    }

    this.state.phase = 'configuring'
    this.emitStateChange()
  }

  /**
   * Submit game configuration and start the game
   */
  public submitConfiguration(config: GameConfiguration): boolean {
    if (this.state.phase !== 'configuring') {
      return false
    }

    // Validate configuration
    if (!config.rounds || config.rounds < 1) {
      return false
    }

    if (!config.timePerRound || config.timePerRound < 1000) {
      return false
    }

    if (!config.colorPickerSequence || config.colorPickerSequence.length === 0) {
      return false
    }

    // Build the picker sequence for each round
    let pickerSequence: ColorPickerType[] = []
    
    // If 'random' is in the sequence, create a full sequence of all picker types
    if (config.colorPickerSequence.includes('random')) {
      // All picker types will be shown in random order each round
      pickerSequence = ['basic', 'wheel', 'saturation', 'hue_slider', 'monochrome']
    } else {
      // Use the selected sequence (will be shown in order each round)
      pickerSequence = [...config.colorPickerSequence]
    }

    this.state.configuration = {
      ...config,
      colorPickerSequence: pickerSequence
    }
    this.state.totalRounds = config.rounds
    this.state.totalTurnsPerRound = pickerSequence.length
    
    // Start the first round
    this.startNextRound()
    return true
  }

  /**
   * Submit a color guess from a player
   */
  public submitGuess(playerId: string, color: RGBColor): GuessSubmitResult {
    // Validate: must be in guessing phase
    if (this.state.phase !== 'guessing') {
      return { success: false, message: 'Not in guessing phase' }
    }

    // Find player
    const player = this.state.players.find(p => p.id === playerId)
    if (!player) {
      return { success: false, message: 'Player not found' }
    }

    // Validate: player hasn't already submitted
    if (player.hasSubmitted) {
      return { success: false, message: 'Already submitted guess' }
    }

    // Validate color values
    if (!this.isValidColor(color)) {
      return { success: false, message: 'Invalid color values' }
    }

    // Store the guess
    player.currentGuess = { ...color }
    player.hasSubmitted = true

    this.emit('guessSubmitted', { playerId, color })
    this.emitStateChange()

    // Check if all players have submitted
    if (this.allPlayersHaveSubmitted()) {
      this.endTurn()
    }

    return { success: true, message: 'Guess submitted' }
  }

  /**
   * Get the current game state (for display purposes)
   */
  public getGameState(): ColorGameState {
    return this.gameStatus
  }

  /**
   * Get the leaderboard
   */
  public getLeaderboard(): ColorPlayerState[] {
    return [...this.state.players].sort((a, b) => b.score - a.score)
  }

  /**
   * Force end current turn (useful for debugging or manual control)
   */
  public forceEndTurn(): void {
    if (this.state.phase === 'guessing') {
      this.endTurn()
    }
  }

  /**
   * Reset and restart the game
   */
  public reset(): void {
    this.clearRoundTimer()
    
    // Reset game state
    this.state = {
      phase: 'waiting',
      configuration: null,
      currentRound: 0,
      totalRounds: 0,
      currentTurnInRound: 0,
      totalTurnsPerRound: 0,
      targetColor: null,
      currentColorPickerType: null,
      pickerBaseHue: null,
      players: this.playersIds.map(id => ({
        id,
        score: 0,
        currentGuess: null,
        hasSubmitted: false
      })),
      timeRemaining: null,
      roundResults: null
    }

    this.emit('gameReset', {})
    this.emitStateChange()
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    this.clearRoundTimer()
    this.removeAllListeners()
  }

  // ===== Private Methods =====

  private startNextRound(): void {
    if (!this.state.configuration) {
      return
    }

    this.state.currentRound++
    this.state.currentTurnInRound = 0

    // Check if game is complete
    if (this.state.currentRound > this.state.totalRounds) {
      this.endGame()
      return
    }

    this.state.phase = 'round_starting'
    
    // Shuffle the picker sequence if random mode
    if (this.state.configuration.colorPickerSequence.length > 1) {
      // Shuffle for this round if we want random order each round
      // For now, we'll keep the order consistent but could shuffle here
    }

    this.emit('roundStart', { 
      round: this.state.currentRound
    })
    this.emitStateChange()

    // Start the first turn of the round
    setTimeout(() => {
      if (this.state.phase === 'round_starting') {
        this.startNextTurn()
      }
    }, 3000)
  }

  private startNextTurn(): void {
    if (!this.state.configuration) {
      return
    }

    this.state.roundResults = null

    // Determine color picker type for this turn
    const pickerIndex = this.state.currentTurnInRound
    let pickerType = this.state.configuration.colorPickerSequence[pickerIndex]
    
    this.state.currentColorPickerType = pickerType

    // For saturation picker, generate a base hue that will be used for both target and picker
    if (pickerType === 'saturation') {
      this.state.pickerBaseHue = Math.floor(Math.random() * 360)
    } else {
      this.state.pickerBaseHue = null
    }

    // Generate target color based on the picker type
    this.state.targetColor = this.generateColorForPickerType(pickerType)

    // Reset player states for this turn
    this.state.players.forEach(p => {
      p.currentGuess = null
      p.hasSubmitted = false
    })

    this.emit('turnStart', { 
      round: this.state.currentRound,
      turn: this.state.currentTurnInRound + 1,
      totalTurns: this.state.totalTurnsPerRound,
      colorPickerType: this.state.currentColorPickerType
    })
    
    // Move to guessing phase
    this.startGuessingPhase()
  }

  private startGuessingPhase(): void {
    if (!this.state.configuration) {
      return
    }

    this.state.phase = 'guessing'
    this.state.timeRemaining = this.state.configuration.timePerRound

    this.emit('guessingStart', { round: this.state.currentRound })
    this.emitStateChange()

    // Start the round timer
    this.startRoundTimer()
  }

  private endTurn(): void {
    this.clearRoundTimer()
    
    if (!this.state.targetColor) {
      return
    }

    this.state.phase = 'turn_ended'
    this.state.timeRemaining = null

    // Calculate results for all players
    const results: RoundResult[] = []
    
    for (const player of this.state.players) {
      const guess = player.currentGuess || { r: 0, g: 0, b: 0 }
      const distance = this.calculateColorDistance(this.state.targetColor, guess)
      const accuracy = this.calculateAccuracy(distance)
      const points = this.calculatePoints(accuracy)
      
      player.score += points
      
      results.push({
        playerId: player.id,
        guess,
        distance,
        points,
        accuracy
      })
    }

    this.state.roundResults = results

    this.emit('turnEnd', { 
      round: this.state.currentRound,
      turn: this.state.currentTurnInRound + 1,
      results,
      targetColor: this.state.targetColor
    })
    this.emitStateChange()

    // Move to next turn or end round
    setTimeout(() => {
      if (this.state.phase === 'turn_ended') {
        this.state.currentTurnInRound++
        
        // Check if round is complete
        if (this.state.currentTurnInRound >= this.state.totalTurnsPerRound) {
          this.endRound()
        } else {
          this.startNextTurn()
        }
      }
    }, 6000)
  }

  private endRound(): void {
    this.state.phase = 'round_ended'
    
    this.emit('roundEnd', { 
      round: this.state.currentRound,
      leaderboard: this.getLeaderboard()
    })
    this.emitStateChange()

    // Start next round after a delay
    setTimeout(() => {
      if (this.state.phase === 'round_ended') {
        this.startNextRound()
      }
    }, 3000)
  }

  private endGame(): void {
    this.clearRoundTimer()
    this.state.phase = 'game_ended'
    
    const leaderboard = this.getLeaderboard()
    const winner = leaderboard[0]

    this.emit('gameEnd', { 
      winner,
      leaderboard 
    })
    this.emitStateChange()
  }

  private allPlayersHaveSubmitted(): boolean {
    return this.state.players.every(p => p.hasSubmitted)
  }

  private startRoundTimer(): void {
    if (!this.state.configuration) {
      return
    }

    this.clearRoundTimer()
    
    const startTime = Date.now()
    const endTime = startTime + this.state.configuration.timePerRound

    this.roundTimer = setInterval(() => {
      const now = Date.now()
      this.state.timeRemaining = Math.max(0, endTime - now)
      
      if (this.state.timeRemaining <= 0) {
        this.endTurn()
      } else {
        this.emitStateChange()
      }
    }, 1000)
  }

  private clearRoundTimer(): void {
    if (this.roundTimer) {
      clearInterval(this.roundTimer)
      this.roundTimer = null
    }
  }

  private emitStateChange(): void {
    const status = this.gameStatus
    this.emit('stateChange', status)
  }

  /**
   * Generate a color that's achievable within the given picker type
   */
  private generateColorForPickerType(pickerType: ColorPickerType): RGBColor {
    switch (pickerType) {
      case 'basic':
        // Basic picker: R and G vary (0-255), B is fixed at 128
        return {
          r: Math.floor(Math.random() * 256),
          g: Math.floor(Math.random() * 256),
          b: 128
        }
      
      case 'wheel':
        // Wheel picker: HSL with any hue, saturation varies with distance from center, lightness fixed at 50%
        const hue = Math.random() * 360
        const saturation = Math.random() * 100 // 0-100%
        const lightness = 50 // Fixed at 50%
        return this.hslToRgb(hue, saturation, lightness)
      
      case 'saturation':
        // Saturation picker: Use the stored base hue, then vary saturation (0-100%) and lightness (0-100%)
        const baseHue = this.state.pickerBaseHue ?? Math.floor(Math.random() * 360)
        const sat = Math.random() * 100
        const light = Math.random() * 100
        return this.hslToRgb(baseHue, sat, light)
      
      case 'hue_slider':
        // Hue slider: All hues (0-360), saturation 100%, lightness 50-100%
        const sliderHue = Math.random() * 360
        const sliderLightness = 50 + Math.random() * 50 // 50-100%
        return this.hslToRgb(sliderHue, 100, sliderLightness)
      
      case 'monochrome':
        // Monochrome: Grayscale (0-255) with some tint
        const gray = Math.floor(Math.random() * 256)
        const tint = Math.floor(Math.random() * 128)
        return {
          r: gray,
          g: Math.min(255, gray + tint),
          b: gray
        }
      
      default:
        // Fallback to random
        return {
          r: Math.floor(Math.random() * 256),
          g: Math.floor(Math.random() * 256),
          b: Math.floor(Math.random() * 256)
        }
    }
  }

  /**
   * Convert HSL to RGB
   */
  private hslToRgb(h: number, s: number, l: number): RGBColor {
    // Normalize values
    h = h % 360
    s = Math.max(0, Math.min(100, s)) / 100
    l = Math.max(0, Math.min(100, l)) / 100

    const c = (1 - Math.abs(2 * l - 1)) * s
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = l - c / 2

    let r = 0, g = 0, b = 0

    if (h >= 0 && h < 60) {
      r = c; g = x; b = 0
    } else if (h >= 60 && h < 120) {
      r = x; g = c; b = 0
    } else if (h >= 120 && h < 180) {
      r = 0; g = c; b = x
    } else if (h >= 180 && h < 240) {
      r = 0; g = x; b = c
    } else if (h >= 240 && h < 300) {
      r = x; g = 0; b = c
    } else {
      r = c; g = 0; b = x
    }

    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255)
    }
  }

  private isValidColor(color: RGBColor): boolean {
    return (
      typeof color.r === 'number' && color.r >= 0 && color.r <= 255 &&
      typeof color.g === 'number' && color.g >= 0 && color.g <= 255 &&
      typeof color.b === 'number' && color.b >= 0 && color.b <= 255
    )
  }

  /**
   * Calculate color distance using Delta E (CIE76) formula
   * This provides perceptually uniform color difference
   * 
   * Returns a value where:
   * - 0 = identical colors
   * - 100+ = very different colors
   * - Max possible distance ~441 (for RGB corners in LAB space)
   */
  private calculateColorDistance(color1: RGBColor, color2: RGBColor): number {
    // Convert RGB to LAB color space for perceptual distance
    const lab1 = this.rgbToLab(color1)
    const lab2 = this.rgbToLab(color2)

    // Calculate Euclidean distance in LAB space (Delta E)
    const deltaL = lab1.l - lab2.l
    const deltaA = lab1.a - lab2.a
    const deltaB = lab1.b - lab2.b

    return Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB)
  }

  /**
   * Convert accuracy (0-1) to points
   * Perfect match (accuracy = 1.0) gets 1.0 points
   * Half accuracy (0.5) gets ~0.5 points
   * Very poor (accuracy = 0) gets 0 points
   * 
   * Uses a smooth curve to reward accuracy fairly
   */
  private calculatePoints(accuracy: number): number {
    // Apply a slight exponential curve to reward high accuracy more
    // This makes getting a near-perfect match more rewarding
    return Math.pow(accuracy, 0.8)
  }

  /**
   * Convert distance to accuracy score (0-1)
   * 
   * Distance mapping:
   * - 0 distance = 1.0 accuracy (perfect)
   * - ~100 distance = 0.5 accuracy (halfway)
   * - ~200+ distance = 0.0 accuracy (very far)
   */
  private calculateAccuracy(distance: number): number {
    // Use an exponential decay function
    // This provides smooth falloff with good discrimination between close colors
    // Distance of ~100 gives accuracy of ~0.5
    // Distance of ~200 gives accuracy of ~0.25
    const k = 0.0069314718 // ln(2) / 100, so distance=100 gives e^(-1*ln(2)) = 0.5
    return Math.exp(-k * distance)
  }

  /**
   * Convert RGB to LAB color space
   * LAB is perceptually uniform, making it better for color difference calculations
   */
  private rgbToLab(rgb: RGBColor): { l: number; a: number; b: number } {
    // First convert RGB to XYZ
    let r = rgb.r / 255
    let g = rgb.g / 255
    let b = rgb.b / 255

    // Apply sRGB gamma correction
    r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92
    g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92
    b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92

    // Convert to XYZ using sRGB matrix
    let x = r * 0.4124564 + g * 0.3575761 + b * 0.1804375
    let y = r * 0.2126729 + g * 0.7151522 + b * 0.0721750
    let z = r * 0.0193339 + g * 0.1191920 + b * 0.9503041

    // Normalize to D65 white point
    x = x / 0.95047
    y = y / 1.00000
    z = z / 1.08883

    // Convert XYZ to LAB
    const epsilon = 0.008856
    const kappa = 903.3

    x = x > epsilon ? Math.pow(x, 1 / 3) : (kappa * x + 16) / 116
    y = y > epsilon ? Math.pow(y, 1 / 3) : (kappa * y + 16) / 116
    z = z > epsilon ? Math.pow(z, 1 / 3) : (kappa * z + 16) / 116

    const l = 116 * y - 16
    const a = 500 * (x - y)
    const bVal = 200 * (y - z)

    return { l, a, b: bVal }
  }
}

